import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

/**
 * Exporta un elemento del DOM a PDF ocupando el 100% del ancho de la página,
 * con márgenes independientes (top/bottom/left/right) y numeración de páginas.
 */
export async function exportElementToPdf(el, opts = {}) {
  if (!el) throw new Error("Elemento no encontrado");

  const {
    filename = "documento.pdf",
    orientation = "p",                 // "p" (vertical) o "l" (horizontal)
    scale = 2,
    margins = { top: 10, right: 0, bottom: 10, left: 0 }, // ← como “antes” arriba/abajo
    fullBleed = true,                  // fuerza ancho exacto de folio
    footer = (pdf, pageIndex, total) => { // numeración por defecto
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      const { right = 0, bottom = 10 } = margins || {};
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text(`Página ${pageIndex} de ${total}`, w - right - 32, h - (bottom > 6 ? bottom - 4 : 6));
    }
  } = opts;

  // ===== Preparar estilos temporales (full width) =====
  const targetWidthMm = orientation === "l" ? 297 : 210;
  const prev = {
    elStyle: el.getAttribute("style"),
    bodyClass: document.body.className,
  };

  // Expandir posibles scrolls internos para que no recorten en la captura
  const overflowNodes = [];
  el.querySelectorAll("*").forEach(n => {
    const cs = window.getComputedStyle(n);
    const hasScroll = /(auto|scroll|hidden)/.test(cs.overflow + cs.overflowY + cs.overflowX);
    if (hasScroll) {
      overflowNodes.push({ n, style: n.getAttribute("style") });
      n.style.overflow = "visible";
      n.style.maxHeight = "none";
    }
  });

  document.body.classList.add("exporting");
  if (fullBleed) el.classList.add("full-bleed");
  el.style.width = `${targetWidthMm}mm`;
  el.style.padding = "0";
  el.style.margin = "0";
  el.style.border = "0";
  el.style.background = "#fff";

  try {
    // Ancho real del bloque (px) para que html2canvas calcule bien
    const bbox = el.getBoundingClientRect();
    const windowWidth = Math.ceil(bbox.width);

    const canvas = await html2canvas(el, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      windowWidth,
    });

    const imgWidthPx = canvas.width;
    const imgHeightPx = canvas.height;

    const pdf = new jsPDF({
      orientation: orientation === "l" ? "landscape" : "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    const left   = margins.left   ?? 0;
    const right  = margins.right  ?? 0;
    const top    = margins.top    ?? 0;
    const bottom = margins.bottom ?? 0;

    const contentW = pageW - left - right;   // 100% ancho menos L/R
    const contentH = pageH - top - bottom;   // deja margen arriba/abajo “como antes”

    // Escala px/mm basada en el ancho útil (contenido)
    const pxPerMm = imgWidthPx / contentW;
    const pageHeightPx = Math.floor(contentH * pxPerMm);

    let renderedPx = 0;
    let pageIndex = 0;
    const totalPages = Math.max(1, Math.ceil(imgHeightPx / pageHeightPx));

    while (renderedPx < imgHeightPx) {
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = imgWidthPx;
      pageCanvas.height = Math.min(pageHeightPx, imgHeightPx - renderedPx);

      const ctx = pageCanvas.getContext("2d");
      ctx.drawImage(
        canvas,
        0, renderedPx,                   // src
        imgWidthPx, pageCanvas.height,   // src size
        0, 0,                            // dst
        imgWidthPx, pageCanvas.height    // dst size
      );

      const pageData = pageCanvas.toDataURL("image/jpeg", 0.95);

      if (pageIndex > 0) pdf.addPage();

      // Altura en mm del trozo
      const pageHeightMm = pageCanvas.height / pxPerMm;

      // Añade imagen respetando márgenes TOP/BOTTOM y sin márgenes laterales (L/R según config)
      pdf.addImage(pageData, "JPEG", left, top, contentW, pageHeightMm);

      // Footer con numeración (dentro del margen inferior)
      if (typeof footer === "function") {
        footer(pdf, pageIndex + 1, totalPages);
      }

      renderedPx += pageCanvas.height;
      pageIndex++;
    }

    pdf.save(filename);
  } finally {
    // Restaurar estilos
    if (prev.elStyle == null) el.removeAttribute("style");
    else el.setAttribute("style", prev.elStyle);
    document.body.className = prev.bodyClass;

    overflowNodes.forEach(({ n, style }) => {
      if (style == null) n.removeAttribute("style");
      else n.setAttribute("style", style);
    });

    el.classList.remove("full-bleed");
  }
}
